import { useFormik } from "formik";
import React, { useCallback, useEffect } from "react";
import { FaUser } from "react-icons/fa";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { editPromo } from "../../apis/promo-codes/edit-promo";
import { serviceTypeMap } from "../../pages/ServicesDashboard";
import { isPromoCodeDescriptionValid } from "../../utils/helpers";
import { IPromoCodeType } from "../../utils/types";
import Button from "../Button";
import Checkbox from "../Checkbox";
import DatePicker from "../DatePicker";
import Input from "../Input";
import Modal from "../Modal";
import Select from "../Select";
import Textarea from "../Textarea";
import Toggle from "../Toggle";

interface PropTypes {
  isVisible: boolean;
  onHide: () => void;
  onSuccess: () => void;
  promoCode: IPromoCodeType | null;
}

export const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split("T")[0];
};

export const serviceTypes = [
  { label: "Package pickup", value: "package_pickup_meta" },
  { label: "Curbside pickup", value: "curbside_pickup_meta" },
  { label: "Instore pickup", value: "instore_pickup_meta" },
];

const discountOptions = [
  {
    label: "Flat Discount",
    value: "flat",
  },
  {
    label: "Percentage Discount",
    value: "percentage",
  },
];

export type FormValues = {
  promoCode: string;
  serviceType: string[]; // Explicitly define serviceType as an array of strings
  discountType: string;
  discountValue: string;
  description: string;
  startDate: string;
  expiryDate: string | null;
  maxUsers: string;
  maxBudget: string;
  isNotifyUser: string;
  isPromoCodeActive: boolean;
  isSingleUse: string;
};

const EditPromoCodeModal = ({
  isVisible,
  onHide,
  onSuccess,
  promoCode,
}: PropTypes) => {
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      promoCode: promoCode?.promoCode ?? "",
      serviceType: serviceTypes.every((item: any) =>
        promoCode?.serviceType.includes(item.value)
      )
        ? ["all"]
        : promoCode?.serviceType || [],
      discountType: promoCode?.discountType ?? "",
      discountValue: promoCode?.discountValue.toString() ?? "",
      description: promoCode?.description ?? "",
      startDate: promoCode?.startDate ? promoCode.startDate : "",
      expiryDate: promoCode?.expiryDate
        ? promoCode.expiryDate
        : null,
      maxUsers: promoCode?.maxUsers?.toString() ?? "",
      maxBudget: promoCode?.maxBudget
        ? (promoCode.maxBudget / 100).toString()
        : "",
      isNotifyUser:
        promoCode?.isNotifyUser === true
          ? "true"
          : promoCode?.isNotifyUser === false
            ? "false"
            : "",
      isPromoCodeActive: promoCode?.isPromoCodeActive ?? false,
      isSingleUse:
        promoCode?.isSingleUse === true
          ? "true"
          : promoCode?.isSingleUse === false
            ? "false"
            : "",
    },
    validationSchema: Yup.object().shape({
      promoCode: Yup.string()
        .trim()
        .matches(/^\S*$/, "Promo code cannot contain spaces")
        .required("This field is required"),
      serviceType: Yup.array()
        .min(1, "At least one service is required")
        .required("This field is required"),
      discountType: Yup.string().required("This field is required"),
      discountValue: Yup.number().positive().required("This field is required"),
      description: Yup.string()
        .trim()
        .required("This field is required")
        .test(
          "description-match",
          "Description doesn't match selected options",
          function (value) {
            const { discountType, discountValue, serviceType } = this.parent;

            const validation = isPromoCodeDescriptionValid(
              value,
              discountType,
              discountValue,
              serviceType,
              serviceTypeMap
            );

            if (!validation.isValid) {
              return this.createError({
                message: validation.error,
              });
            }

            return true;
          }
        ),
      startDate: Yup.date().required("This field is required"),
      maxUsers: Yup.number().positive(),
      maxBudget: Yup.number().positive(),
      expiryDate: Yup.date()
        .min(Yup.ref("startDate"), "Expiry date must be after the start date")
        .test(
          "is-greater",
          "Expiry date must be after the start date",
          function (value) {
            const { startDate } = this.parent;
            if (!value) return true;
            return new Date(value) > new Date(startDate);
          }
        )
        .nullable(),
      isNotifyUser: Yup.string().required("This field is required"),
      isSingleUse: Yup.string().required("This field is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      const {
        serviceType,
        maxBudget,
        isNotifyUser,
        maxUsers,
        discountValue,
        isSingleUse,
        ...rest
      } = values;
      const res = await editPromo({
        ...rest,
        serviceType: serviceType.includes("all")
          ? serviceTypes.map((service) => service.value)
          : serviceType,
        maxUsers: parseInt(maxUsers),
        maxBudget: parseInt(maxBudget),
        discountValue: parseInt(discountValue),
        isNotifyUser: isNotifyUser === "true" ? true : false,
        isSingleUse: isSingleUse === "true" ? true : false,
        id: promoCode?._id!,
      });
      if (res.success) {
        toast("Promo Code Edited Successfully", { type: "success" });
        resetForm();
        onSuccess();
        onHide();
      } else {
        toast(res.error?.message, {
          type: "error",
        });
      }
    },
  });

  const handleServiceTypeChange = useCallback(
    (service: { label: string; value: string }, checked: boolean) => {
      let currentServiceTypes = [...formik.values.serviceType];
      if (currentServiceTypes.includes("all")) {
        currentServiceTypes = [];
      }
      if (checked) {
        formik.setFieldValue("serviceType", [
          ...currentServiceTypes,
          service.value,
        ]);
      } else {
        formik.setFieldValue(
          "serviceType",
          currentServiceTypes.filter((item) => item !== service.value)
        );
      }
    },
    [formik.values.serviceType, formik.setFieldValue]
  );

  const isEditButtonDisabled = formik.dirty;

  useEffect(() => {
    if (
      formik.values.discountType &&
      formik.values.discountValue &&
      formik.values.description === ""
    ) {
      formik.setFieldValue(
        "description",
        formik.values.discountType === "flat"
          ? `Enjoy $${formik.values.discountValue} discount on this offer`
          : `Enjoy ${formik.values.discountValue}% discount on this offer`
      );
    }
  }, [formik.values.discountType, formik.values.discountValue]);

  return (
    <Modal
      heading="Edit promo code"
      isVisible={isVisible}
      containerClassName="lg:max-w-4xl"
      onHide={() => {
        formik.resetForm();
        onHide();
      }}
    >
      <form onSubmit={formik.handleSubmit} className='px-8'>
        <div className='flex flex-col md:grid md:grid-cols-[70%_30%] gap-x-4'>
          <div className='flex  flex-col md:grid md:grid-col-2 gap-x-4'>
            <Input
              placeholder="Enter promo code"
              isInvalid={Boolean(
                formik.touched.promoCode && formik.errors.promoCode
              )}
              className="input-primary"
              label="Promo code *"
              name="promoCode"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.promoCode}
              value={formik.values.promoCode}
            />

            <DatePicker
              name="startDate"
              label="Valid from *"
              selected={
                formik.values.startDate ? new Date(formik.values.startDate) : null
              }
              onChange={(date) =>
                formik.setFieldValue(
                  "startDate",
                  date ? date.toISOString() : ""
                )
              }
              onBlur={formik.handleBlur}
              error={formik.errors.startDate}
              isInvalid={Boolean(
                formik.touched.startDate && formik.errors.startDate
              )}
              dateFormat="MM/dd/yyyy"
              placeholderText="MM/DD/YYYY"
              popperPlacement="bottom"
            />

            <Select
              placeholder="Select a discount type"
              isInvalid={Boolean(
                formik.touched.discountType && formik.errors.discountType
              )}
              className="select-primary text-[1rem] lg:text-xs"
              containerClassName='mt-1'
              label="Discount type *"
              name="discountType"
              value={formik.values.discountType}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.discountType}
              options={discountOptions}
            />
            <Input
              containerClassName="pb-0 mt-1"
              rightContent={
                <div className="flex-shrink-0 border-l border-primary flex h-full items-center justify-center bg-gray-200 w-8 rounded-r-lg text-black">
                  {formik.values.discountType === "flat"
                    ? "$"
                    : formik.values.discountType === "percentage"
                      ? "%"
                      : ""}
                </div>
              }
              placeholder="Enter value"
              isInvalid={Boolean(
                formik.touched.discountValue && formik.errors.discountValue
              )}
              label="Of *"
              className="input-primary"
              name="discountValue"
              value={formik.values.discountValue}
              type="number"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.discountValue}
            />
            <Input
              containerClassName="pb-0 mt-1"
              rightContent={
                <div className="flex-shrink-0 border-l border-primary flex h-full items-center justify-center bg-gray-200 w-8 rounded-r-lg text-black">
                  <FaUser />
                </div>
              }
              placeholder="Enter value"
              type="number"
              isInvalid={Boolean(
                formik.touched.maxUsers && formik.errors.maxUsers
              )}
              className="input-primary"
              label="Max users"
              name="maxUsers"
              onChange={formik.handleChange}
              value={formik.values.maxUsers}
              onBlur={formik.handleBlur}
              error={formik.errors.maxUsers}
            />
            <Input
              containerClassName="pb-0 mt-1"
              rightContent={
                <div className="flex-shrink-0 border-l border-primary flex h-full items-center justify-center bg-gray-200 w-8 rounded-r-lg text-black">
                  $
                </div>
              }
              placeholder="Enter value"
              type="number"
              className="input-primary"
              isInvalid={Boolean(
                formik.touched.maxBudget && formik.errors.maxBudget
              )}
              label="Max budget"
              name="maxBudget"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.maxBudget}
              value={formik.values.maxBudget}
            />
            <div className='col-start-1 col-end-3'>
              <Textarea
                containerClassName="col-span-6"
                placeholder="Test discount"
                className="textarea-primary"
                isInvalid={Boolean(
                  formik.touched.description && formik.errors.description
                )}
                label="Discount description *"
                name="description"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.description}
                value={formik.values.description}
              />
            </div>
          </div>
          <div className='flex flex-col'>
            <DatePicker
              name="expiryDate"
              label="Valid till (optional)"
              selected={
                formik.values.expiryDate ? new Date(formik.values.expiryDate) : null
              }
              onChange={(date: Date | null) =>
                formik.setFieldValue(
                  "expiryDate",
                  date ? date.toISOString() : null
                )
              }
              onBlur={formik.handleBlur}
              error={formik.errors.expiryDate}
              isInvalid={Boolean(
                formik.touched.expiryDate && formik.errors.expiryDate
              )}
              dateFormat="MM/dd/yyyy"
              placeholderText="MM/DD/YYYY"
              popperPlacement="bottom"
            />
            <div>
              <div className="label">
                <span className="label-text">Service type *</span>
              </div>

              <Checkbox
                containerClassName="w-14"
                onChange={(e) => {
                  if (e.target.checked) {
                    formik.setFieldValue("serviceType", ["all"]);
                  } else {
                    formik.setFieldValue("serviceType", []);
                  }
                }}
                label="All"
                checked={formik.values.serviceType.includes("all")}
              />
              {serviceTypes.map((service) => (
                <Checkbox
                  containerClassName="w-32"
                  key={service.value}
                  onChange={(e) =>
                    handleServiceTypeChange(service, e.target.checked)
                  }
                  label={service.label}
                  checked={formik.values.serviceType.includes(service.value)}
                />
              ))}

              {formik.touched.serviceType && formik.errors.serviceType ? (
                <div className="label">
                  <span className="label-text-alt text-red-600">
                    {formik.errors.serviceType}
                  </span>
                </div>
              ) : null}
            </div>

            <div className="-space-y-1">
              <Toggle
                labelContainerClassName="flex justify-start gap-4"
                checked={formik.values.isNotifyUser === "true"}
                name="isNotifyUser"
                onChange={(e) =>
                  formik.setFieldValue(
                    "isNotifyUser",
                    e.target.checked.toString()
                  )
                }
                isInvalid={Boolean(
                  formik.touched.isNotifyUser && formik.errors.isNotifyUser
                )}
                error={formik.errors.isNotifyUser}
                label="Notify user"
              />
              <Toggle
                labelContainerClassName="flex justify-start gap-[1.32rem]"
                checked={formik.values.isSingleUse === "true"}
                name="isSingleUse"
                onChange={(e) =>
                  formik.setFieldValue(
                    "isSingleUse",
                    e.target.checked.toString()
                  )
                }
                isInvalid={Boolean(
                  formik.touched.isSingleUse && formik.errors.isSingleUse
                )}
                error={formik.errors.isSingleUse}
                label="Single use"
              />
            </div>

          </div>

          <div className="col-start-1 col-end-3 flex justify-center mt-3">
            {/* <Button
            onClick={() => {
              formik.resetForm();
              onHide();
            }}
            type="button"
            variant="outline"
            className="w-20"
          >
            Close
          </Button> */}
            <Button
              disabled={!isEditButtonDisabled}
              isLoading={formik.isSubmitting}
              type="submit"
              className="w-full md:w-32 min-h-0 h-10 md:h-10"
            >
              Edit
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default EditPromoCodeModal;
